/**
 * Who is training, and under which plan.
 *
 * `/train/live` is a full-viewport presentation *over* the Train destination
 * (§6.1's route table), not a destination of its own, so it has no props and no
 * route parameters to read a plan from. The surface that starts a workout —
 * Train home, the builder, or a resumed checkpoint — publishes the request
 * here, and the live route consumes it.
 *
 * A context rather than a module-level singleton: two documents of the same
 * origin are two sessions, and a singleton would let one tab's plan decide what
 * the other tab is running.
 */

import type { ReactNode } from "react";
import { createContext, useContext } from "react";
import type { LiveWorkoutRequest } from "./session";

const LiveWorkoutContext = createContext<LiveWorkoutRequest | null>(null);

export interface LiveWorkoutProviderProps {
  /** `null` means nothing is being trained, which is a real, ordinary state. */
  readonly request: LiveWorkoutRequest | null;
  readonly children?: ReactNode;
}

export function LiveWorkoutProvider({
  request,
  children,
}: LiveWorkoutProviderProps): ReactNode {
  return <LiveWorkoutContext.Provider value={request}>{children}</LiveWorkoutContext.Provider>;
}

/**
 * `null` when no workout is active. Deliberately not an error: the live route
 * is mounted by the shell's full-viewport table and can legitimately be
 * evaluated before anything has been started.
 */
export function useLiveWorkoutRequest(): LiveWorkoutRequest | null {
  return useContext(LiveWorkoutContext);
}
